import { useState } from "react";
import api, { formatError, fileUrl } from "@/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DEPARTMENTS } from "@/lib/specializations";
import { Plus, Upload } from "lucide-react";
import { toast } from "sonner";

export default function AddDoctorModal({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", department: "", mobile: "", email: "",
    avg_diagnosis_time: 10, profile_pic: "",
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, profile_pic: data.path }));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        avg_diagnosis_time: parseInt(form.avg_diagnosis_time) || 10,
        email: form.email || undefined,
      };
      const { data } = await api.post("/doctors", payload);
      toast.success(`Doctor added. CID: ${data.cid}`);
      setOpen(false);
      setForm({ name: "", department: "", mobile: "", email: "", avg_diagnosis_time: 10, profile_pic: "" });
      onCreated?.();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-sky-500 hover:bg-sky-600 text-white gap-2" data-testid="add-doctor-trigger">
          <Plus className="w-4 h-4" /> Add Doctor
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Doctor</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-lg border-2 border-dashed bg-slate-50 flex items-center justify-center overflow-hidden">
              {form.profile_pic ? (
                <img src={fileUrl(form.profile_pic)} alt="" className="w-full h-full object-cover" />
              ) : (
                <Upload className="w-6 h-6 text-slate-400" />
              )}
            </div>
            <label className="text-sm text-sky-600 hover:underline cursor-pointer" data-testid="add-doctor-pic">
              {uploading ? "Uploading..." : "Upload profile picture"}
              <input type="file" accept="image/*" className="hidden" onChange={onFile} />
            </label>
          </div>

          <div>
            <Label>Doctor Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="add-doctor-name" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Department</Label>
              <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                <SelectTrigger data-testid="add-doctor-department"><SelectValue placeholder="Choose..." /></SelectTrigger>
                <SelectContent>
                  {DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Avg Diagnosis Time (min)</Label>
              <Input type="number" min="1" value={form.avg_diagnosis_time} onChange={(e) => setForm({ ...form, avg_diagnosis_time: e.target.value })} data-testid="add-doctor-avgtime" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Mobile Number</Label>
              <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} required maxLength={10} minLength={10} data-testid="add-doctor-mobile" inputMode="numeric" />
            </div>
            <div>
              <Label>Official Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="add-doctor-email" />
            </div>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={submitting || !form.department} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="add-doctor-submit">
              {submitting ? "Adding..." : "Add Doctor"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
